import React from "react";
import { useSelector } from "react-redux";
import {
    useTheme,
    Box,
    Typography,
    IconButton,
    Tooltip,
    Link ,
    Divider,
} from "@mui/material";
import {
    Visibility,
    Edit,
    GroupAdd,
    Wc,
    MailOutline,
    Cake,
    Phone,
} from '@mui/icons-material';
import { hasPermission } from "state/checkPermission";
import { formatTZDate } from 'state/helpers';
import FlexBetween from "components/FlexBetween";


const CrewMemberList = ({ rows, onView, onEdit, onDependents }) => {
    const user = useSelector((state) => state.user);
    const theme = useTheme();


    return (
        <Box display="flex" flexDirection="column" gap={2}>
            {rows?.map((member, index) => {
                const birthDate = formatTZDate(member.birthday);
                const inactive = member.isActive === false;

                const canView = hasPermission(user?.permissions, "CrewMembers", "view", false);
                const canEdit = hasPermission(user?.permissions, "CrewMembers", "update", false);
                const canDependents = hasPermission(user?.permissions, "CrewMembers", "dependents", false);

                const iconColor = (inactive)
                    ? theme.palette.text.disabled
                    : theme.palette.primary.dark;
                    
                return (
                    <Box
                        key={member._id || index}
                        sx={{
                            border: `1px solid ${inactive
                                ? theme.palette.neutral.light
                                : theme.palette.neutral.deep}`,
                            borderRadius: 2,
                            p: 1.5,
                            backgroundColor: inactive
                                ? theme.palette.action.hover
                                : "background.paper",
                            color: inactive
                                ? theme.palette.text.disabled
                                : theme.palette.text.primary,
                            opacity: inactive ? 0.75 : 1,
                        }}
                    >
                        <FlexBetween>
                            <Typography
                                variant="subtitle2"
                                sx={{
                                    color: inactive
                                        ? theme.palette.text.disabled
                                        : theme.palette.text.primary,
                                }}
                            >
                                {member.crewCode} - {member.crewName}
                            </Typography>
                            <Typography
                                variant="caption"
                                sx={{
                                    color: inactive
                                        ? theme.palette.text.disabled
                                        : theme.palette.text.secondary,
                                }}
                            >
                                {member.rank} | {member.location || "-"}
                            </Typography>
                        </FlexBetween>

                        <Divider sx={{ my: 0.5 }} />

                        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                            <Box display="flex" alignItems="center" gap={1}>
                                <Wc
                                    fontSize="small"
                                    sx={{
                                        color: inactive
                                            ? theme.palette.text.disabled
                                            : member.gender === "FM"
                                                ? theme.palette.secondary.main
                                                : theme.palette.primary.dark,
                                    }}
                                />
                                <Typography variant="body2">
                                    {member.gender || "-"}
                                </Typography>
                            </Box>

                            <Box display="flex" alignItems="center" gap={1}>
                                <Cake
                                    fontSize="small"
                                    sx={{
                                        color: inactive
                                            ? theme.palette.text.disabled
                                            : "#EAB308",
                                    }}
                                />
                                <Typography variant="body2">{birthDate || "-"}</Typography>
                            </Box>

                            <Box display="flex" alignItems="center" gap={1}>
                                <MailOutline
                                    fontSize="small"
                                    sx={{
                                        color: inactive
                                            ? theme.palette.text.disabled
                                            : "#0891B2",
                                    }}
                                />
                                <Typography variant="body2">{member.email || "-"}</Typography>
                            </Box>

                            <Box display="flex" alignItems="center" gap={1}>
                                <Phone
                                    fontSize="small"
                                    sx={{
                                        color: inactive
                                            ? theme.palette.text.disabled
                                            : "#16A34A",
                                    }}
                                />
                                <Typography variant="body2">
                                    {member.contactTel ? (
                                        <Link
                                            href={`tel:${member.contactTel}`}
                                            underline="hover"
                                            sx={{
                                                color: theme.palette.text.primary,
                                                cursor: 'pointer',
                                                '&:hover': { color: theme.palette.green.deep },
                                            }}
                                        >
                                            {member.contactTel}
                                        </Link>
                                    ) : (
                                        "-"
                                    )}
                                </Typography>
                            </Box>
                        </Box>

                        <Box
                            display="flex"
                            justifyContent="flex-end"
                            alignItems="center"
                            gap={2}
                            sx={{ mt: 1 }}
                        >
                            {canDependents && (
                                <Tooltip title="Dependents">
                                <IconButton
                                    size="small"
                                    disabled={inactive}
                                    onClick={() =>
                                        canDependents && onDependents(member)
                                    }
                                >
                                    <GroupAdd fontSize="small" sx={{ color: iconColor }} />
                                </IconButton>
                                </Tooltip>
                            )}

                            {canEdit && (
                                <Tooltip title="Edit">
                                    <IconButton
                                        size="small"
                                        onClick={() =>
                                            canEdit && onEdit(member)
                                        }
                                    >
                                        <Edit
                                            fontSize="small"
                                            sx={{ color: theme.palette.primary.dark }}
                                        />
                                    </IconButton>
                                </Tooltip>
                            )}
                            
                            {canView && (
                                <Tooltip title="View">
                                    <IconButton
                                        size="small"
                                        disabled={inactive}
                                        onClick={() =>
                                            canView && onView(member)
                                        }
                                    >
                                        <Visibility fontSize="small" sx={{ color: iconColor }} />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </Box>
                    </Box>
                );
            })}
        </Box>
    );
};

export default CrewMemberList;
